import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { RevenueSummary as RevenueSummaryModel } from '@/api/models';
import { RevenueSummarySkeleton } from './RevenueSummarySkeleton';
import { RevenueSummaryView } from './RevenueSummaryView';
import { useRevenueSummary } from './useRevenueSummary';

type RevenueSummaryProps = {
    'data-testid'?: string;
    id?: string;
};

type RevenueSummaryContentProps = {
    data: RevenueSummaryModel | null;
    'data-testid': string;
    error: ErrorResponse | null;
    id: string;
    isLoading: boolean;
};

/**
 * Self-served revenue summary widget.
 *
 * Owns its own data hook (`useRevenueSummary`) and is wrapped in an
 * `ErrorBoundary` so a render-time failure in this widget cannot break the
 * surrounding page. While the data is loading the container renders
 * `RevenueSummarySkeleton`, which mirrors the final layout to prevent shift.
 *
 * Hook-returned errors (network failures, non-2xx responses) are re-thrown
 * inside `RevenueSummaryContent` so the surrounding `ErrorBoundary` catches
 * them and shows its localized fallback with a Retry button.
 */
export const RevenueSummary: React.FC<RevenueSummaryProps> = ({
    'data-testid': dataTestId = 'revenue-summary',
    id = 'revenue-summary',
}) => {
    const { data, error, isLoading, onRetry } = useRevenueSummary();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.revenueSummary.errorTitle'),
            errorMessage: t('sales.widgets.revenueSummary.errorMessage'),
            errorRetry: t('sales.widgets.revenueSummary.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid={`${dataTestId}-error-boundary`}
            fallbackMessage={copy.errorMessage}
            id={`${id}-error-boundary`}
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <RevenueSummaryContent
                data={data}
                data-testid={dataTestId}
                error={error}
                id={id}
                isLoading={isLoading}
            />
        </ErrorBoundary>
    );
};

/**
 * Inner component sits *inside* the `ErrorBoundary` so the `throw` below is
 * caught by it — React error boundaries do not catch errors thrown by the
 * component that renders them, only by their children.
 */
const RevenueSummaryContent: React.FC<RevenueSummaryContentProps> = ({
    data,
    'data-testid': dataTestId,
    error,
    id,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return (
            <RevenueSummarySkeleton
                data-testid={`${dataTestId}-loading`}
                id={`${id}-loading`}
            />
        );
    }

    return <RevenueSummaryView data={data} />;
};
